import React from 'react';
import { ChevronDown, ChevronRight, DollarSign, FileText } from 'lucide-react';
import { cn } from '../../../utils/cn';

/**
 * JobGroupRow
 *
 * Middle-level collapsible row for a Job within a receiver group.
 * Displays job name, priority, payment, and instance count.
 */
const JobGroupRow = ({
  job,
  instanceCount,
  isExpanded,
  onToggle,
  colorScheme,
  isLast = false,
  showChevron = true,
  showInvoice = false,
  onViewInvoice
}) => {
  const getPriorityBadge = () => {
    const priority = job?.priority?.toLowerCase();
    const styles = {
      high: 'bg-red-500/20 text-red-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      low: 'bg-green-500/20 text-green-400'
    };

    return (
      <span className={cn(
        'px-2 py-0.5 text-xs font-medium rounded-full uppercase',
        styles[priority] || 'bg-gray-500/20 text-text-secondary'
      )}>
        {priority || 'normal'}
      </span>
    );
  };

  return (
    <div
      onClick={showChevron ? onToggle : undefined}
      className={cn(
        'pl-6 pr-3 py-2.5 transition-colors',
        showChevron && 'cursor-pointer hover:bg-opacity-80',
        colorScheme?.job,
        !isLast && 'border-b border-dashed border-accent/40'
      )}
    >
      <div className="flex items-start gap-2">
        {/* Chevron (only if multiple instances) */}
        <div className="w-5 flex-shrink-0 pt-0.5">
          {showChevron && instanceCount > 1 ? (
            <button className="text-text-secondary hover:text-text-primary">
              {isExpanded ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </button>
          ) : null}
        </div>

        {/* Job details */}
        <div className="flex-1 min-w-0">
          {/* Top row: Job name and priority */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text-primary font-medium text-sm truncate">
              {job?.job_name || 'Unnamed Job'}
            </span>
            {getPriorityBadge()}
          </div>

          {/* Bottom row: Payment and instance count */}
          <div className="flex items-center gap-3 mt-1 text-xs">
            <div className="flex items-center text-green-400">
              <DollarSign size={12} className="mr-0.5" />
              <span className="font-medium">{job?.payment_amount || '0'}</span>
              <span className="text-text-secondary ml-0.5">
                {job?.payment_type === 'hourly' ? '/hr' : ''}
              </span>
            </div>
            <span className="text-text-secondary">
              {instanceCount === 1 ? 'One-off' : 'Recurring'}
            </span>
            {instanceCount > 1 && (
              <span className="text-text-secondary text-xs">
                ({instanceCount} instances)
              </span>
            )}
          </div>
        </div>

        {/* Invoice Button */}
        {showInvoice && onViewInvoice && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewInvoice();
            }}
            className="flex items-center gap-1 px-2 py-1 bg-border hover:bg-surface text-text-secondary hover:text-text-primary text-xs rounded transition-colors flex-shrink-0"
            title="View Job Invoice"
          >
            <FileText size={12} />
            <span className="hidden sm:inline">Invoice</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default JobGroupRow;
